import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { locationsApi, usersApi } from '@/api/endpoints'
import type { User, UserInput, UserRole } from '@/api/types'
import { useAuth } from '@/auth/AuthContext'
import { assignableRoles, ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/auth/permissions'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { MultiSelect, SelectField, TextField } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { PasswordRules } from '@/components/ui/PasswordRules'
import { useToast } from '@/components/ui/Toast'
import { isPasswordValid } from '@/lib/password'

interface Props {
  open: boolean
  onClose: () => void
  /** `null` oznacza zakładanie nowego konta. */
  user: User | null
}

interface FormState {
  email: string
  first_name: string
  last_name: string
  phone: string
  role: UserRole
  also_teaches: boolean
  location_ids: string[]
  password: string
}

const EMPTY: FormState = {
  email: '',
  first_name: '',
  last_name: '',
  phone: '',
  role: 'TEACHER',
  also_teaches: false,
  location_ids: [],
  password: '',
}

export function UserFormModal({ open, onClose, user }: Props) {
  const queryClient = useQueryClient()
  const { user: actor } = useAuth()
  const { notifySuccess } = useToast()
  const isEdit = user !== null

  const [form, setForm] = useState<FormState>(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Lista oddziałów jest już przefiltrowana przez backend do zasięgu użytkownika,
  // więc administrator zobaczy wyłącznie swoje lokalizacje.
  const locationsQuery = useQuery({
    queryKey: ['locations', 'picker'],
    queryFn: () => locationsApi.list({ size: 200 }),
    enabled: open,
  })

  const roles = assignableRoles(actor)

  useEffect(() => {
    if (!open) return
    setError(null)
    setFieldErrors({})
    setForm(
      user
        ? {
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            phone: user.phone ?? '',
            role: user.role,
            also_teaches: user.also_teaches,
            location_ids: user.locations.map((location) => location.id),
            password: '',
          }
        : { ...EMPTY, role: roles.includes('TEACHER') ? 'TEACHER' : (roles[0] ?? 'TEACHER') },
    )
    // `roles` wynika ze stałej roli zalogowanego użytkownika — nie zmienia się
    // w trakcie życia formularza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const mutation = useMutation({
    mutationFn: () => {
      const base = {
        email: form.email.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
        role: form.role,
        // Serwer i tak wymusza fałsz dla ról innych niż administrator, ale
        // wysyłanie prawdy przy nauczycielu byłoby mylące w dzienniku audytu.
        also_teaches: form.role === 'ADMIN' && form.also_teaches,
        location_ids: form.location_ids,
      }

      if (isEdit) {
        return usersApi.update(user.id, base)
      }
      const payload: UserInput = {
        ...base,
        password: form.password,
        // Hasło startowe nadaje administrator, więc użytkownik musi je zmienić.
        must_change_password: true,
      }
      return usersApi.create(payload)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] })
      notifySuccess(isEdit ? 'Zapisano zmiany konta.' : 'Konto zostało utworzone.')
      onClose()
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setError(caught.message)
        setFieldErrors(caught.fieldErrors)
      } else {
        setError('Nie udało się zapisać konta.')
      }
    },
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    mutation.mutate()
  }

  const needsLocation = form.role !== 'OWNER'
  const canSubmit =
    form.email.trim() !== '' &&
    form.first_name.trim() !== '' &&
    form.last_name.trim() !== '' &&
    (!needsLocation || form.location_ids.length > 0) &&
    (isEdit || isPasswordValid(form.password))

  const locationOptions = (locationsQuery.data?.items ?? []).map((location) => ({
    value: location.id,
    label: location.name,
  }))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edytuj konto' : 'Nowe konto'}
      description={
        isEdit
          ? 'Zmiana oddziałów natychmiast wpływa na zakres danych widocznych dla tej osoby.'
          : 'Nowe konto otrzyma hasło startowe i zostanie poproszone o jego zmianę przy pierwszym logowaniu.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            form="user-form"
            isLoading={mutation.isPending}
            disabled={!canSubmit}
          >
            {isEdit ? 'Zapisz zmiany' : 'Utwórz konto'}
          </Button>
        </>
      }
    >
      <form id="user-form" onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="error">{error}</Alert>}

        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            label="Imię"
            value={form.first_name}
            onChange={(event) => update('first_name', event.target.value)}
            error={fieldErrors.first_name}
            required
            autoFocus
          />
          <TextField
            label="Nazwisko"
            value={form.last_name}
            onChange={(event) => update('last_name', event.target.value)}
            error={fieldErrors.last_name}
            required
          />
        </div>

        <TextField
          label="Adres e-mail"
          type="email"
          value={form.email}
          onChange={(event) => update('email', event.target.value)}
          hint="Służy jako login do systemu."
          error={fieldErrors.email}
          required
        />

        <TextField
          label="Telefon"
          type="tel"
          value={form.phone}
          onChange={(event) => update('phone', event.target.value)}
          placeholder="+48 123 456 789"
          error={fieldErrors.phone}
        />

        <SelectField
          label="Rola"
          value={form.role}
          onChange={(event) => update('role', event.target.value as UserRole)}
          options={roles.map((role) => ({ value: role, label: ROLE_LABELS[role] }))}
          hint={ROLE_DESCRIPTIONS[form.role]}
          error={fieldErrors.role}
          required
        />

        {form.role === 'ADMIN' && (
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-outline-variant p-4 transition-colors hover:border-primary/40">
            <input
              type="checkbox"
              checked={form.also_teaches}
              onChange={(event) => update('also_teaches', event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--color-primary,#800020)]"
            />
            <span>
              <span className="block text-sm font-semibold text-on-surface">
                Prowadzi również zajęcia
              </span>
              <span className="block text-xs text-on-surface-variant">
                Konto pojawi się w wyborze prowadzącego, w grafiku i w raporcie do wypłat.
                Właścicielka zajęć nie prowadzi, więc opcja dotyczy wyłącznie administratorów.
              </span>
            </span>
          </label>
        )}

        {needsLocation ? (
          <MultiSelect
            label="Oddziały"
            options={locationOptions}
            selected={form.location_ids}
            onChange={(values) => update('location_ids', values)}
            hint="Konto zobaczy dane wyłącznie z zaznaczonych oddziałów."
            error={fieldErrors.location_ids}
            disabled={locationsQuery.isPending}
            placeholder="Wybierz oddziały"
            emptyMessage="Brak dostępnych oddziałów."
          />
        ) : (
          <Alert tone="info">
            Konto właściciela ma dostęp do wszystkich oddziałów — obecnych i przyszłych.
          </Alert>
        )}

        {/* Kontener obramowany, nie wypełniony: pola formularza mają własne
            szare tło i na wypełnionym kontenerze byłyby niewidoczne. */}
        {!isEdit && (
          <div className="space-y-3 rounded-lg border border-outline/20 p-4">
            <TextField
              label="Hasło startowe"
              type="text"
              value={form.password}
              onChange={(event) => update('password', event.target.value)}
              hint="Przekaż je bezpiecznym kanałem. System wymusi zmianę przy pierwszym logowaniu."
              error={fieldErrors.password}
              autoComplete="off"
              required
            />
            <PasswordRules password={form.password} />
          </div>
        )}
      </form>
    </Modal>
  )
}
